/*
 *		Copyright (c) 2011-2019 Acronis Incorporated. All rights reserved.
 *
 *	This source code may be used only by organizations licensed to use Acronis Access, in conjunction with normal Acronis Access usage.
 *	No portion of it may be redistributed or modified without the written permission of Acronis, Inc. See the Acronis Software License Agreement for more information.
 */

'use strict';

/**
 * @typedef {object} SignedFileExt
 * @property {'main'} type Main type indicator
 * @property {Array.<SignedDocument>} documents List of the documents
 * 
 * @typedef {SignedFileResource & SignedFileExt} SignedFile
 */

/**
 * @typedef {Object.<string, SignedFilesHashmapValue>} SignedFilesHashmap
 */

/**
 * @typedef {(SignedFile | SignedDocument)} SignedItem
 */

/**
 * @typedef {object} SignedFilesHashmapValue
 * @property {number} index Index of the element inside of $scope.table.entries array
 * @property {boolean} documentsLoaded True if the documents were requested
 * @property {boolean} documentsVisible Display the list of the documents or not
 */

/**
 * OnClick handler for an item: main or the document
 * @callback SignedItemClickHandler
 * @param {SignedItem} item Instance of the item
 */

/**
 * @typedef {object} SignedFilesScope
 * @property {object} table Table settings
 * @property {Array.<SignedItem>} table.filteredEntries List of all visible items
 * @property {SignedItemClickHandler} onItemClick Item click handler
 */

angular.module('accessWebClient')
    .controller('SignedFilesController', ['$scope', '$translate', '$document', '$location', 'headerService', 'nodeModalAction', 'Esignature', 'ElementsThatKeepTheSelectedState', 'EsignatureDetailsModal',
        /**
         * SignedFiles controller
         * @param {SignedFilesScope} $scope
         * @param {Object} $translate
         * @param {Object} $document
         * @param {Object} $location
         * @param {Object} headerService
         * @param {Function} nodeModalAction
         * @param {Object} Esignature
         * @param {Object} ElementsThatKeepTheSelectedState
         * @param {Object} EsignatureDetailsModal
         */
        function ($scope, $translate, $document, $location, headerService, nodeModalAction, Esignature, ElementsThatKeepTheSelectedState, EsignatureDetailsModal) {
            $translate('node_signed_files_section_title').then(function (translation) {
                headerService.addBreadcrumbsRootItem(translation, null);
            });

            /**
             *  Query parameter key, that (when present) triggers the search for an eSignature in the loaded documents
             *  @type {String}
             */
            const searchEsignatureParameter = 'highlight_esignature_uuid';

            /**
             *  UUID of eSignature to be found and selected
             *  @type {String}
             */
            const searchEsignatureUUID = $location.search()[searchEsignatureParameter];

            /**
             *  State of the main items in the list
             *  @type {SignedFilesHashmap}
             */
            var mainItemsState = {};

            $scope.selectedItem = null;

            $scope.table = {
                model: Esignature,
                perPage: 100,
                supportsSearch: false
            };

            $scope.$watch('table.entries',
                /**
                 * @param {Array.<SignedFile>} [newEntries] New list of the main items
                 * @param {Array.<SignedFile>} [oldEntries] Old list of the main items
                 */
                function (newEntries, oldEntries) {
                    if (!newEntries) {
                        return;
                    }

                    /** @type {Array.<SignedItem>} */
                    var filteredItems = [];

                    newEntries.forEach(function (item, index) {
                        var stateKey = _generateStateKey(item);
                        var itemState = mainItemsState[stateKey];

                        Object.defineProperties(item, {
                            type: {
                                value: 'main'
                            },
                            documentsVisible: {
                                get: function () {
                                    return mainItemsState[_generateStateKey(this)].documentsVisible;
                                },
                                configurable: true
                            }
                        });

                        var itemsToPush = [item];

                        if (itemState) {
                            item.documents = oldEntries[itemState.index].documents;
                            itemState.index = index;

                            // If the documents list was visible
                            // push them to the all list items
                            if (itemState.documentsVisible) {
                                itemsToPush.push.apply(itemsToPush, item.documents);
                            }
                        } else {
                            mainItemsState[stateKey] = {
                                index: index,
                                documentsLoaded: false,
                                documentsVisible: false
                            };
                        }

                        filteredItems.push.apply(filteredItems, itemsToPush);
                    });

                    $scope.table.filteredEntries = filteredItems;
                }
            );

            $scope.onItemClick = function (item, $event) {
                if (angular.isObject($event)) {
                    $event.stopPropagation();
                }

                if (item.type === 'main') {
                    _toggleDocumentsList(item);
                }
            };

            $scope.selectItem = function (item) {
                if (!$scope.selectedItem || !$scope.isItemSelected(item)) {
                    $scope.selectedItem = item;
                }
            };

            // Expand the rows for the signed revision found by the `accSelectNodeFromUrl` directive
            $scope.toggleSelect = function (item) {
                $scope.selectItem(item);

                if (searchEsignatureUUID) {
                    _toggleDocumentsList(item);
                }
            }

            $scope.isItemSelected = function (item) {
                return item === $scope.selectedItem;
            };

            $scope.showDocumentLinks = function (item, $event) {
                $event.stopPropagation();

                nodeModalAction(
                    {
                        node: angular.extend({}, item.mainItem, { esignatureID: item.uuid })
                    },
                    {
                        modalController: 'NodeLinksModalController',
                        modalType: 'confirm',
                        modalSize: 'lg',
                        modalAction: 'esignatureLinksList',
                        modalClass: 'link_details-dialog',
                        modalBodyTemplate: 'links/details',
                        modalTitle: $translate.instant('Shared links: %{node_name}', {
                            node_name: item.mainItem.name
                        }),
                        modalSubTitle: $translate.instant('Revision') + ' ' + item.node_revision,
                        modalNotifications: true
                    }
                );
            };

            $document.off('click', 'body').on('click', 'body', function ($event) {
                var clickedElement = $($event.target);

                for (var i in ElementsThatKeepTheSelectedState) {
                    if (clickedElement.is(ElementsThatKeepTheSelectedState[i])) return;
                }

                $scope.selectedItem = null;
                $scope.$apply();
            });

            $scope.$on('reload.document', function (_, item) {
                _loadDocuments(item);
            });

            /**
             * Generate unique key for the state
             * @param {SignedFile} item Signed file instance
             * @return {string} key
             */
            function _generateStateKey(item) {
                return item.uuid + '-' + item.revision;
            }

            /**
             * Toggle the document list of the main item
             * @param {SignedFile} mainItem Instance of the signing file
             */
            function _toggleDocumentsList(mainItem) {
                var mainItemState = mainItemsState[_generateStateKey(mainItem)];
                var mainItemIndex = $scope.table.filteredEntries.indexOf(mainItem);

                if (mainItemState.documentsLoaded) {
                    mainItemState.documentsVisible = !mainItemState.documentsVisible;

                    if (mainItemState.documentsVisible) {
                        _showDocuments(mainItem)
                    } else {
                        $scope.table.filteredEntries = $scope.table.filteredEntries.filter(function (item) {
                            return mainItem.documents.indexOf(item) === -1;
                        });
                    }
                } else {
                    _loadDocuments(mainItem);
                }
            }

            function _showDocuments(mainItem) {
                var mainItemState = mainItemsState[_generateStateKey(mainItem)];
                var mainItemIndex = $scope.table.filteredEntries.indexOf(mainItem);
                var visibleDocuments = $scope.table.filteredEntries.filter(function (item) {
                    return item.mainItem === mainItem;
                });
                var insertIndex = Math.max($scope.table.filteredEntries.indexOf(visibleDocuments[0]), mainItemIndex + 1);

                Array.prototype.splice.apply(
                    $scope.table.filteredEntries,
                    [
                        insertIndex,
                        visibleDocuments.length
                    ].concat(mainItem.documents)
                );
                mainItemState.documentsVisible = true;

                // Find the nested document row, highlight it and open the eSignature Details modal
                if(searchEsignatureUUID) {
                    const nestedDocument = _selectNestedDocument(searchEsignatureUUID, mainItem.documents);

                    if (nestedDocument) {
                        EsignatureDetailsModal.open(nestedDocument);
                    }

                    $location.search(searchEsignatureParameter, null);
                }
            }

            function _removeMainItem(mainItemToRemove) {
                $scope.table.filteredEntries = $scope.table.filteredEntries.filter(function (item) {
                    return item !== mainItemToRemove && item.mainItem !== mainItemToRemove;
                });
            }

            /**
             * Load documents for the certain item
             * @param {SignedFile} mainItem Instance of the signing file
             */
            function _loadDocuments(mainItem) {
                var mainItemState = mainItemsState[_generateStateKey(mainItem)];
                var mainItemIndex = $scope.table.filteredEntries.indexOf(mainItem);

                // Manually append loading item element into $scope.table.filteredEntries.
                var loadingItem = {
                    uuid: Math.round(Math.random() * 1e5).toString(), // Requires as ng-repeat rely on it
                    type: 'loading'
                };

                $scope.table.filteredEntries.splice(
                    mainItemIndex + 1,
                    0,
                    loadingItem
                );

                Esignature.getDocuments({
                    node_uuid: mainItem.uuid,
                    revision: mainItem.revision
                })
                    .$promise
                    .then(function (documents) {
                        documents.forEach(function (document) {
                            document.mainItem = mainItem;
                        });

                        if (documents.length) {
                            mainItem.documents = documents;
                            mainItemState.documentsLoaded = true;
                            _showDocuments(mainItem);
                        } else {
                            _removeMainItem(mainItem);
                        }
                    })
                    .finally(function () {
                        // Remove loading element from the list
                        $scope.table.filteredEntries = $scope.table.filteredEntries.filter(function (item) {
                            return item !== loadingItem;
                        });
                        // We're trying to keep current user selection,
                        // and reset it when previously selected item is no long exists
                        $scope.selectedItem = $scope.table.filteredEntries.filter(function (item) {
                            return item.uuid === $scope.selectedItem.uuid;
                        })[0] || null;
                    });
            }

            function _selectNestedDocument(needle, documents) {
                for (let i = 0; i < documents.length; i++) {
                    if (documents[i].uuid === needle) {
                        $scope.selectItem(documents[i]);
                        return documents[i];
                    }
                }
            }
        }
    ]);
